import { useEffect, useState } from "react";
import { Box, Text, Tabs } from "@mantine/core";
import { parseJsonText } from "@utils/jsonText";
import { CodeEditor } from "../../CodeEditor";
import { translateStepType } from "@utils/flowTypeTranslations";
import { useTranslation } from "@hooks/useTranslation";
import type { FlowStepFormData, AgentExecutionFormData } from "@utils/builders/flowConfig";
import type { CatalogProvider } from "@hooks/flow/useModelCatalog";
import { LLMConfigTab } from "../../LLMConfigTab/LLMConfigTab";
import { OCRConfigTab } from "../../OCRConfigTab/OCRConfigTab";
import { AgentExecutionsEditor } from "../../AgentExecutionsEditor";
import { StepRuntimeOptionsFields } from "../../StepRuntimeOptionsFields";
import type { StepRuntimeOptionsValue } from "@models/doctype";
import "./StepConfiguration.scss";

interface StepConfigurationProps {
    step: FlowStepFormData;
    stepNumber: number;
    activeTab: string;
    providers: CatalogProvider[];
    /** Model suggestions for the selected provider. */
    models: string[];
    /** Provider key, as the step stores it. */
    selectedProvider: string;
    isLoadingModels: boolean;
    isLoadingProviders: boolean;
    onTabChange: (tab: string) => void;
    onProviderChange: (providerKey: string) => void;
    onModelChange: (modelName: string) => void;
    onFieldChange: (field: string, value: string | object | null) => void;
    onRuntimeOptionsChange: (value: StepRuntimeOptionsValue | null) => void;
    onAgentsChange?: (agents: AgentExecutionFormData[]) => void;
}

export const StepConfiguration = ({
    step,
    stepNumber,
    activeTab,
    providers,
    models,
    selectedProvider,
    isLoadingModels,
    isLoadingProviders,
    onTabChange,
    onProviderChange,
    onModelChange,
    onFieldChange,
    onRuntimeOptionsChange,
    onAgentsChange,
}: StepConfigurationProps) => {
    const { t } = useTranslation();

    const isLLMStep = step.llmStepConfig !== undefined;
    const isOCRStep = step.ocrStepConfig !== undefined;
    const hasAgents = Array.isArray(step.llmStepConfig?.executions);

    const handleConfigOptionChange = (field: string, value: number | string) => {
        const currentOptions = step.llmStepConfig?.configOptions || {};
        const numValue = typeof value === "number" ? value : value ? parseFloat(value) : null;
        onFieldChange("configOptions", {
            ...currentOptions,
            [field]: numValue,
        });
    };

    const tabLabels = [
        t("admin.flowConfigs.labels.llmConfig"),
        t("admin.flowConfigs.labels.systemPrompt"),
        t("admin.flowConfigs.labels.userPrompt"),
        t("admin.flowConfigs.labels.responseSchema"),
        t("admin.flowConfigs.labels.runtimeOptions"),
    ];

    // The schema is edited as text: half-typed JSON must stay in the editor
    // (with its error shown) instead of snapping back to the last valid value.
    const schemaToText = () =>
        step.llmStepConfig?.responseSchema ? JSON.stringify(step.llmStepConfig.responseSchema, null, 2) : "";
    const [schemaText, setSchemaText] = useState(schemaToText);
    useEffect(() => {
        setSchemaText(schemaToText());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stepNumber]);

    return (
        <Box className="step-configuration">
            <Text fw={600} size="lg" className="step-configuration__title">
                {t("admin.flowConfigs.create.configureStep", { order: stepNumber, type: translateStepType(step.stepType, t) })}
            </Text>

            {isLLMStep && (
                <>
                    <Tabs
                        value={activeTab}
                        onChange={(value) => onTabChange(value || "0")}
                        className="step-configuration__tabs"
                    >
                        <Tabs.List>
                            {tabLabels.map((label, index) => (
                                <Tabs.Tab key={index} value={String(index)}>
                                    {label}
                                </Tabs.Tab>
                            ))}
                            {hasAgents && onAgentsChange && (
                                <Tabs.Tab value="agents">
                                    {t("admin.flowConfigs.agents.title")} ({(step.llmStepConfig?.executions || []).length})
                                </Tabs.Tab>
                            )}
                        </Tabs.List>

                        <Tabs.Panel value="0" className="step-configuration__tab-panel">
                            <LLMConfigTab
                                stepType={step.stepType}
                                isLoadingProviders={isLoadingProviders}
                                isLoadingModels={isLoadingModels}
                                providers={providers}
                                models={models}
                                selectedProvider={selectedProvider}
                                modelName={step.llmStepConfig?.modelName ?? ""}
                                temperature={step.llmStepConfig?.configOptions?.temperature ?? ""}
                                topP={step.llmStepConfig?.configOptions?.topP ?? ""}
                                topK={step.llmStepConfig?.configOptions?.topK ?? ""}
                                maxOutputTokens={step.llmStepConfig?.configOptions?.maxOutputTokens ?? ""}
                                thinkingBudget={step.llmStepConfig?.configOptions?.thinkingBudget ?? ""}
                                seed={step.llmStepConfig?.configOptions?.seed ?? ""}
                                configOptions={step.llmStepConfig?.configOptions || null}
                                onProviderChange={onProviderChange}
                                onModelChange={onModelChange}
                                onTemperatureChange={(v) => handleConfigOptionChange('temperature', v)}
                                onTopPChange={(v) => handleConfigOptionChange('topP', v)}
                                onTopKChange={(v) => handleConfigOptionChange('topK', v)}
                                onMaxOutputTokensChange={(v) => handleConfigOptionChange('maxOutputTokens', v)}
                                onThinkingBudgetChange={(v) => handleConfigOptionChange('thinkingBudget', v)}
                                onSeedChange={(v) => handleConfigOptionChange('seed', v)}
                                onConfigOptionsChange={(value) => onFieldChange("configOptions", value)}
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="1" className="step-configuration__tab-panel">
                            <CodeEditor
                                language="text"
                                label={t("admin.flowConfigs.labels.systemPrompt")}
                                value={step.llmStepConfig?.systemPrompt || ""}
                                onChange={(value) => onFieldChange("systemPrompt", value)}
                                required
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="2" className="step-configuration__tab-panel">
                            <CodeEditor
                                language="text"
                                label={t("admin.flowConfigs.labels.userPrompt")}
                                value={step.llmStepConfig?.userPrompt || ""}
                                onChange={(value) => onFieldChange("userPrompt", value)}
                                required
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="3" className="step-configuration__tab-panel">
                            <CodeEditor
                                language="json"
                                label={t("admin.flowConfigs.labels.responseSchema")}
                                description={t("admin.flowConfigs.editor.schemaOptional")}
                                value={schemaText}
                                onChange={(value) => {
                                    setSchemaText(value);
                                    const parsed = parseJsonText(value);
                                    if (parsed.ok) onFieldChange("responseSchema", parsed.value);
                                }}
                                placeholder='{"type": "object", "properties": {...}}'
                            />
                        </Tabs.Panel>

                        <Tabs.Panel value="4" className="step-configuration__tab-panel">
                            <StepRuntimeOptionsFields
                                stepType={step.stepType}
                                value={Object.fromEntries(
                                    Object.entries({
                                        fallbackModel: step.llmStepConfig?.fallbackModel,
                                        chunkingOptions: step.llmStepConfig?.chunkingOptions,
                                        pageDetectionPolicy: step.llmStepConfig?.pageDetectionPolicy,
                                    }).filter(([, value]) => value !== undefined && value !== null),
                                )}
                                onChange={onRuntimeOptionsChange}
                            />
                        </Tabs.Panel>

                        {hasAgents && onAgentsChange && (
                            <Tabs.Panel value="agents" className="step-configuration__tab-panel">
                                <AgentExecutionsEditor
                                    agents={step.llmStepConfig?.executions || []}
                                    modelSuggestions={models}
                                    onAgentsChange={onAgentsChange}
                                />
                            </Tabs.Panel>
                        )}
                    </Tabs>
                </>
            )}

            {isOCRStep && (
                <OCRConfigTab
                    isLoadingProviders={isLoadingProviders}
                    isLoadingModels={isLoadingModels}
                    providers={providers}
                    models={models}
                    selectedProvider={selectedProvider}
                    modelName={step.ocrStepConfig?.modelName ?? ""}
                    ocrOptions={step.ocrStepConfig?.ocrOptions || null}
                    onProviderChange={onProviderChange}
                    onModelChange={onModelChange}
                    onOcrOptionsChange={(value) => onFieldChange("ocrOptions", value)}
                />
            )}
        </Box>
    );
};
